import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { PersonalAccessTokenEligibleRight, PersonalAccessTokenScopes } from 'src/app/core/_models/personalAccessToken';
import { PersonalAccessTokenService } from 'src/app/core/_services/personal-access-token.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';

@Component({
  selector: 'app-dialog-create-personal-access-token',
  templateUrl: './dialog-create-personal-access-token.component.html',
  styleUrls: ['./dialog-create-personal-access-token.component.scss']
})
export class DialogCreatePersonalAccessTokenComponent implements OnInit {
  form!: FormGroup;
  eligibleRights: PersonalAccessTokenEligibleRight[] = [];
  scopes = PersonalAccessTokenScopes;
  minExpireDate = new Date(new Date().setHours(0, 0, 0, 0) + 24 * 3600 * 1000);
  token?: string;
  copied = false;
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<DialogCreatePersonalAccessTokenComponent>,
    private formBuilder: FormBuilder,
    private personalAccessTokenService: PersonalAccessTokenService,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.form = this.formBuilder.group(
      {
        name: [null, [Validators.required, Validators.maxLength(100)]],
        rightId: [null, Validators.required],
        projectsRead: [true],
        projectsWrite: [false],
        catalogsWrite: [false],
        expireDate: [null]
      },
      { validators: [this.atLeastOneScope] }
    );
  }

  ngOnInit(): void {
    this.personalAccessTokenService.getEligibleRights().subscribe({
      next: (rights) => {
        this.eligibleRights = rights ?? [];
        if (this.eligibleRights.length === 1) this.form.patchValue({ rightId: this.eligibleRights[0].rightId });
      },
      error: () => this.snackbarService.open(SnackbarTypes.error)
    });
  }

  get formCrls() {
    return this.form?.controls;
  }

  get selectedRight() {
    return this.eligibleRights.find((right) => right.rightId === this.formCrls.rightId.value);
  }

  get missingCatalogRights(): string[] {
    const right = this.selectedRight;
    if (!right) return [];
    return [
      { granted: right.managesProducts, key: 'personal_access_token_catalogs_products' },
      { granted: right.managesEquipment, key: 'personal_access_token_catalogs_equipment' },
      { granted: right.managesTransports, key: 'personal_access_token_catalogs_transports' },
      { granted: right.managesRoutings, key: 'personal_access_token_catalogs_routings' }
    ]
      .filter((catalog) => !catalog.granted)
      .map((catalog) => this.translate.instant(catalog.key));
  }

  atLeastOneScope(group: AbstractControl): ValidationErrors | null {
    return group.get('projectsRead')?.value || group.get('projectsWrite')?.value || group.get('catalogsWrite')?.value ? null : { scopeRequired: true };
  }

  create() {
    if (this.form.invalid || this.saving) return;

    const value = this.form.value;
    const scopes = [];
    if (value.projectsRead) scopes.push(PersonalAccessTokenScopes.projectsRead);
    if (value.projectsWrite) scopes.push(PersonalAccessTokenScopes.projectsWrite);
    if (value.catalogsWrite) scopes.push(PersonalAccessTokenScopes.catalogsWrite);
    // The key works until the end of the chosen day, in the user's time zone; the API stores UTC.
    const expireDate = value.expireDate ? new Date(new Date(value.expireDate).setHours(23, 59, 59, 0)).toISOString() : undefined;

    this.saving = true;
    this.personalAccessTokenService.create({ rightId: value.rightId, name: value.name.trim(), scopes, expireDate }).subscribe({
      next: (created) => {
        this.saving = false;
        this.token = created?.token;
      },
      error: (error) => {
        this.saving = false;
        const messages = Array.isArray(error?.error) ? error.error.map((e: any) => e?.errorMessage).filter((m: string) => m) : [];
        if (messages.length > 0) this.snackbarService.showMessage(messages.join(' '));
        else this.snackbarService.open(SnackbarTypes.error);
      }
    });
  }

  copy() {
    if (!this.token) return;
    navigator.clipboard.writeText(this.token).then(
      () => (this.copied = true),
      () => this.snackbarService.showMessage(this.translate.instant('personal_access_token_copy_failed'))
    );
  }

  close() {
    this.dialogRef.close(!!this.token);
  }
}
